import apiPrd from '../../services/apiPrd'
import apiPrdNew from '../../services/apiPrdNew'
import apiDriverCpf from '../../services/DriverCpfService/apiDriverCpf'
import { getApiErrorMessage } from '../../utils/apiError'

export const isVehicleGroupResponse = (data) =>
  Boolean(data && typeof data === 'object' && (data.id || data.primaryVehicle))

export const getVehicleGroupApiErrorMessage = getApiErrorMessage

const LoadDetailsService = {
  async findOrRegisterByVehicleIds(vehicleIds, shippingHash, deliveryId) {
    const searchParams = new URLSearchParams()

    vehicleIds.forEach((vehicleId) => {
      searchParams.append('vehicleIds', vehicleId)
    })

    if (shippingHash) {
      searchParams.set('shippingHash', shippingHash)
    }

    if (deliveryId) {
      searchParams.set('deliveryId', deliveryId)
    }

    const res = await apiPrd.axiosInstance.post(
      `/vehicle/findOrRegisterByVehicleIds?${searchParams.toString()}`,
      null,
      { validateStatus: () => true },
    )

    const data = res.data

    if (res.status >= 400 || !isVehicleGroupResponse(data)) {
      const error = new Error(getApiErrorMessage(data))
      error.status = res.status
      error.data = data
      throw error
    }

    return data
  },
  async fetchLoadDetailsById(id, deliveryId) {
    const url = deliveryId ? `/shipping/${id}?deliveryId=${deliveryId}` : `/shipping/${id}`
    const res = await apiPrd.get(url)
    return res.data
  },

  async deleteFilesToLoad(hashId, attachmentId) {
    const res = await apiPrd.delete(`/shipping/${hashId}/attachments/${attachmentId}`)
    return res.data
  },
  async requestWithoutTracking({ deliveryId, hashId, motive }) {
    const res = await apiPrd.post(
      encodeURI(
        `/shipping/${hashId}/requestWithoutTracking?deliveryId=${deliveryId}&motive=${motive}`,
      ),
    )
    return res.data
  },
  async updateFilesToLoad(hashId, payload, attachmentId) {
    const res = await apiPrd.put(`/shipping/${hashId}/attachments/${attachmentId}`, payload)
    return res.data
  },
  async attachFilesToLoad(hashId, payload) {
    const res = await apiPrd.post(`/shipping/${hashId}/attachments`, payload)
    return res.data
  },
  async fetchLoadAttachments(hashId, deliveryId) {
    const res = await apiPrd.get(`/shipping/${hashId}/attachments?deliveryId=${deliveryId || ''}`)
    return res.data
  },
  async fetchLoadNegotiations(id) {
    const res = await apiPrd.get(`/shipping/${id}/negotiation`)
    return res.data
  },
  async fetchLoadBids(id) {
    const res = await apiPrd.get(`/shipping/${id}/bid`)
    return res.data
  },
  async sendNewProposal(id, value, estimatedDate, observation) {
    const res = await apiPrd.post(`/shipping/${id}/negotiation/makeproposal`, {
      value,
      estimatedDate,
      observation,
    })
    return res.data
  },
  async sendNewProposalByVehicleType(hashId, payload) {
    const res = await apiPrd.post(
      `/shipping/${hashId}/negotiation/makeproposal`,
      payload,
    )
    return res.data
  },
  async getNegotiationConfigByVehicleAnttId(hashId, vehicleAnttId) {
    const res = await apiPrd.get(
      `/shipping/${hashId}/getNegotiationConfigByVehicleAnttId?vehicleAnttId=${vehicleAnttId}`,
    )
    return res.data
  },
  async getNegotiationIcmsByMargin(negotiationConfigId, percentMargin) {
    const res = await apiPrd.get(
      `/shipping/negotiationConfig/${negotiationConfigId}/icms?percentMargin=${percentMargin}`,
    )
    return res.data
  },
  async getNegotiationIcmsByTotal(negotiationConfigId, total) {
    const res = await apiPrd.get(
      `/shipping/negotiationConfig/${negotiationConfigId}/icms/byTotal?total=${total}`,
    )
    return res.data
  },
  async sendDoesntHaveATracker(id, payload) {
    const res = await apiPrd.post(`/shipping/${id}/events`, payload)
    return res.data
  },
  async fetchItnerary(hashId, deliveryId) {
    let url = `/shipping/${hashId}/itinerary`
    if (deliveryId) {
      url = `/shipping/${hashId}/itinerary?deliveryId=${deliveryId}`
    }
    const res = await apiPrd.get(url)
    return res.data
  },
  async fetchTrackerOptions(deliveryId) {
    const res = await apiPrd.get(`/shipping/delivery/${deliveryId}/itinerary/new`)
    return res.data
  },
  async acceptLoad(hashId, possibleFreightPriceId) {
    const url = [
      `/shipping/${hashId}/accept`,
      possibleFreightPriceId ? `?possibleFreightPriceId=${possibleFreightPriceId}` : '',
    ].join('')
    const res = await apiPrd.post(url)
    return res
  },
  async findDriverByCpf(cpf, shippingHash, deliveryId) {
    let url = `/driver/${cpf}`
    const deliveryIdParam = deliveryId ? `&deliveryId=${deliveryId}` : ''
    if (shippingHash) {
      url = `/driver/${cpf}?shippingHash=${shippingHash}${deliveryIdParam}`
    }
    const res = await apiPrd.get(url)
    return res.data
  },
  async findDriverByName(data) {
    const res = await apiPrd.post('/driver/search', data)
    return res.data
  },
  async findVehicleByPlate(plate, shippingHash, deliveryId) {
    let url = `/vehicle/search?plate=${plate}&page=1&size=10`
    const deliveryIdParam = deliveryId ? `&deliveryId=${deliveryId}` : ''
    if (shippingHash) {
      url = `/vehicle/search?plate=${plate}&page=1&size=10&shippingHash=${shippingHash}${deliveryIdParam}`
    }
    const res = await apiPrd.get(url)
    return res.data
  },
  async consultPlateApiInfoSimples(plate, rntrc, vehicleId, deliveryId) {
    const res = await apiPrd.post(`/vehicle/${vehicleId}/consultPlateApiInfoSimples?plate=${encodeURIComponent(plate)}&rntrc=${encodeURIComponent(rntrc)}&deliveryId=${deliveryId}`)
    return res
  },

  async findDriverByCpfAtlas(cpf) {
    // PRD 856b9eb748c18eadaaeb2e26dbda3593
    // DEV 5ae973d7a997af13f0aaf2bf60e65803
    const res = await apiDriverCpf.get(`/856b9eb748c18eadaaeb2e26dbda3593/2/json/${cpf}`)
    return res.data
  },
  async bindDriverAndVehicle(hashId, driverId, vehicleId, freightPrice, deliveryId) {
    let url = `/shipping/${hashId}/bindvehicledriver?driverId=${driverId}&vehicleId=${vehicleId}&freightPrice=${freightPrice}`
    if (deliveryId) {
      url = `/shipping/${hashId}/bindvehicledriver?driverId=${driverId}&vehicleId=${vehicleId}&freightPrice=${freightPrice}&deliveryId=${deliveryId}`
    }
    const res = await apiPrd.post(url, {})
    return res.data
  },
  async markPurposesAsRead(hashId) {
    const res = await apiPrd.post(`/shipping/${hashId}/negotiation/markproposal`, {})
    return res.data
  },
  async requestReopen(hashId, reason) {
    const res = await apiPrd.post(`/shipping/${hashId}/requestReopen?reason=${reason}`)
    return res.data
  },
  async forceDriverAccept(hashId, driverId) {
    const res = await apiPrd.post(`/shipping/${hashId}/confirmbinddriver?driverId=${driverId}`, {})
    return res.data
  },
  async fetchLoadHistory(hashId, deliveryId) {
    const res = await apiPrd.get(`/shipping/${hashId}/events?deliveryId=${deliveryId}`)
    return res.data
  },
  async placeBid(hashId, bid) {
    const res = await apiPrd.post(`/shipping/${hashId}/bid?value=${bid}`, {})
    return res.data
  },
  async fetchLoadIncidents(deliveryId) {
    const res = await apiPrd.get(`/occurrence/findByDelivery?deliveryId=${deliveryId}`)
    return res.data
  },
  async openIncident(incident) {
    const res = await apiPrd.post('/occurrence', incident)
    return res.data
  },
  async closeIncident(incidentId, obs) {
    const res = await apiPrd.post(`/occurrence/${incidentId}/close?observation=${obs}`)
    return res.data
  },
  async forwardIncident(incidentId, obs) {
    const res = await apiPrd.post(`/occurrence/${incidentId}/forward?observation=${obs}`)
    return res.data
  },
  async batchWithdraw(hashId, amount, lotAgreementId) {
    const params = new URLSearchParams({ amount: String(amount) })
    if (lotAgreementId != null) {
      params.set('lotAgreementId', String(lotAgreementId))
    }

    const res = await apiPrd.post(`/shipping/${hashId}/withdraw?${params.toString()}`, {})
    return res.data
  },
  async updateWithdraw({ hashId, amount, shippingLotWithdrawalId }) {
    const res = await apiPrd.post(
      encodeURI(
        `/shipping/${hashId}/updateWithdrawal?amount=${amount}&shippingLotWithdrawalId=${shippingLotWithdrawalId}`,
      ),
      {},
    )
    return res.data
  },
  async closeLoadByTransporter(hashId, data) {
    const res = await apiPrd.post(`/shipping/${hashId}/closebytransporter`, data)
    return res.data
  },
  async closeUniqueLoadByTransporter(hashId, deliveryId, payload) {
    const res = await apiPrd.post(
      `/shipping/${hashId}/deliveries/${deliveryId}/closeByTransporter`,
      payload,
    )
    return res.data
  },
  async updateReceiptImages(hashId, deliveryId, receiverId, paths) {
    const res = await apiPrd.put(
      `/shipping/${hashId}/deliveries/${deliveryId}/receivers/${receiverId}`,
      {
        receiptImage: paths,
      },
    )
    return res.data
  },
  async fetchReceivers(hashId, deliveryId) {
    const res = await apiPrd.get(`shipping/${hashId}/deliveries/${deliveryId}/receivers`)
    return res.data
  },
  async removeDriveFromLoad(hashId, driverId, deliveryId) {
    const res = await apiPrd.post(
      `/shipping/${hashId}/unbindvehicledriver?driverId=${driverId}&deliveryId=${deliveryId}`,
      {},
    )
    return res.data
  },
  async requestForcedAccept(hashId, deliveryId, motive) {
    const res = await apiPrd.post(
      encodeURI(
        `/shipping/${hashId}/requestBindDriverForced?deliveryId=${deliveryId}&motive=${motive}`,
        {},
      ),
    )
    return res.data
  },
  async fetchLogisticOperators(operationGroupId) {
    const res = await apiPrd.get(`/logisticOperator?operationGroupId=${operationGroupId}`)
    return res.data
  },
  async confirmSendToLogisticOperator(hashId, deliveryId, sendToLogisticOperatorRequest) {
    const res = await apiPrd.post(
      `/shipping/${hashId}/deliveries/${deliveryId}/sendToLogisticOperator`,
      sendToLogisticOperatorRequest,
    )
    return res.data
  },
  async downloadShippingOnOperator(hashId, deliveryId) {
    const query = deliveryId ? `?deliveryId=${deliveryId}` : ''
    const res = await apiPrd.post(`/shipping/${hashId}/downloadShippingOnOperator${query}`)
    return res.data
  },
  async ungroupShippings(hashId) {
    const res = await apiPrd.post(`/shipping/${hashId}/ungroup`, {})
    return res.data
  },
  async ungroupSingleShipping(hashId, deliveryId) {
    const res = await apiPrd.delete(
      `/shipping/removeGroup?shippingHash=${hashId}&deliveryIds=${deliveryId}`,
    )
    return res.data
  },
  async addGroup(hashId, deliveryIds) {
    const res = await apiPrd.post(
      `/shipping/addGroup?shippingHash=${hashId}&deliveryIds=${deliveryIds}`,
    )
    return res.data
  },
  async getAllPendingsByShipping(deliveryId) {
    const { data, status } = await apiPrd.get(`/pending/findByShipping?deliveryId=${deliveryId}`)
    return { data, status }
  },
  async getAllPendingsTypes(deliveryId) {
    const { data, status } = await apiPrd.get(`/pending/types?deliveryId=${deliveryId}`)

    return { data, status }
  },
  async createNewPending(payload) {
    const { data, status } = await apiPrd.post('/pending/open', payload)
    return { data, status }
  },
  async removePending(id) {
    const { data, status } = await apiPrd.delete(`/pending/${id}`)
    return { data, status }
  },
  async findOrderByCode(orderCode, shipperId = '') {
    const res = await apiPrd.get(
      `/order/findByCodeAndShipperId/${orderCode}?shipperId=${shipperId}`,
    )
    return res
  },
  async getShippingOrder({ hashId, deliveryId }) {
    const res = await apiPrdNew.post(
      [
        `/shipping/${hashId}/shippingOrder`,
        deliveryId ? `?deliveryId=${deliveryId}` : undefined,
      ].join(''),
    )
    return res
  },
  async saveDataCiot(deliveryId, payload) {
    const res = await apiPrd.post(
      `/shipping/saveDataCiot?deliveryId=${deliveryId}`,
      payload,
    )
    return res
  },
}

export default LoadDetailsService
